import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

const Menu = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const [openSubmenus, setOpenSubmenus] = useState({})

  // Menu structure matching the original JSP
  const menuItems = [
    {
      id: 'top',
      title: 'ＴＯＰ',
      path: '/top',
      type: 'link'
    },
    {
      id: 'satsueiKanri',
      title: '撮影管理',
      type: 'submenu',
      items: [
        { id: 'satsueiDataToroku', title: '撮影データ登録', path: '/satsueiDataToroku' },
        { id: 'satsueiDataKensaku', title: '撮影データ検索一覧', path: '/satsueiDataKensaku' },
        { id: 'gazoKensaku', title: '画像検索一覧', path: '/gazoKensaku' },
      ]
    },
    {
      id: 'master',
      title: 'マスター管理',
      type: 'submenu',
      items: [
        {
          id: 'satsueiKanriMaster',
          title: '撮影管理',
          type: 'submenu',
          items: [
            { id: 'satsueiMemoCategory', title: '撮影メモ管理', path: '/satsueiMemoCategory' },
            { id: 'imageFolder', title: '画像フォルダ管理', path: '/imageFolder' },
            { id: 'satsueiGuide', title: '撮影時ガイド管理', path: '/satsueiGuide' }
          ]
        },
        {
          id: 'userKanriMaster',
          title: 'ユーザー管理',
          type: 'submenu',
          items: [
            { id: 'kigyoJohoKanri', title: '企業情報管理', path: '/kigyoJohoKanri' },
            { id: 'kyotenKanri', title: '拠点管理', path: '/kyotenKanri' },
            { id: 'userKanri', title: 'ユーザー管理', path: '/userKanri' }
          ]
        }
      ]
    },
    {
      id: 'inquiry',
      title: 'お問い合わせ',
      path: '/inquiry',
      type: 'link'
    }
  ]

  const toggleSubmenu = (menuId) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }))
  }

  const handleMenuClick = (path) => {
    navigate(path)
    if (onClose) {
      onClose()
    }
  }

  const renderMenuItem = (item) => {
    if (item.type === 'link') {
      return (
        <li key={item.id} className="sb-menu-item">
          <button
            onClick={() => handleMenuClick(item.path)}
            className="sb-menu-link w-full text-left p-3 hover:bg-gray-100 transition-colors"
          >
            {item.title}
          </button>
        </li>
      )
    } else if (item.type === 'submenu') {
      const isOpen = openSubmenus[item.id]
      
      return (
        <li key={item.id} className="sb-menu-item">
          <button
            onClick={() => toggleSubmenu(item.id)}
            className="sb-toggle-submenu w-full text-left p-3 hover:bg-gray-100 transition-colors flex justify-between items-center"
          >
            <span>{item.title}</span>
            <span className={`sb-caret transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
          
          <AnimatePresence>
            {isOpen && (
              <motion.ul
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="sb-submenu overflow-hidden"
              >
                {item.items.map((subItem) => {
                  if (subItem.type === 'submenu') {
                    const isSubOpen = openSubmenus[subItem.id]
                    
                    return (
                      <li key={subItem.id} className="sb-submenu-item">
                        <button
                          onClick={() => toggleSubmenu(subItem.id)}
                          className="sb-toggle-submenu w-full text-left p-2 pl-6 hover:bg-gray-50 transition-colors flex justify-between items-center text-sm"
                        >
                          <span>{subItem.title}</span>
                          <span className={`sb-caret transition-transform duration-200 ${isSubOpen ? 'rotate-180' : ''}`}>
                            ▼
                          </span>
                        </button>
                        
                        <AnimatePresence>
                          {isSubOpen && (
                            <motion.ul
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: 'easeInOut' }}
                              className="sb-submenu overflow-hidden"
                            >
                              {subItem.items.map((subSubItem) => (
                                <li key={subSubItem.id} className="sb-submenu-item">
                                  <button
                                    onClick={() => handleMenuClick(subSubItem.path)}
                                    className="sb-menu-link w-full text-left p-2 pl-8 hover:bg-gray-50 transition-colors text-sm"
                                  >
                                    {subSubItem.title}
                                  </button>
                                </li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </li>
                    )
                  } else {
                    return (
                      <li key={subItem.id} className="sb-submenu-item">
                        <button
                          onClick={() => handleMenuClick(subItem.path)}
                          className="sb-menu-link w-full text-left p-2 pl-6 hover:bg-gray-50 transition-colors text-sm"
                        >
                          {subItem.title}
                        </button>
                      </li>
                    )
                  }
                })}
              </motion.ul>
            )}
          </AnimatePresence>
        </li>
      )
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50"
        >
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50" 
            onClick={onClose}
            data-testid="menu-backdrop"
          />
          
          {/* Menu Panel */}
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-y-0 left-0 w-80 bg-white shadow-lg overflow-y-auto"
          >
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-800">メニュー</h2>
              <button 
                onClick={onClose} 
                className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-200 transition-colors"
                aria-label="Close menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Menu Content */}
            <nav className="p-0" role="navigation">
              <ul className="sb-menu">
                {menuItems.map(renderMenuItem)}
              </ul>
            </nav>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default Menu
